<script setup>
import { computed, onMounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import ReportMetricCard from './components/ReportMetricCard.vue';
import PdvSalesProductRanking from './components/PdvSalesProductRanking.vue';
import PdvSalesTimelineChart from './components/PdvSalesTimelineChart.vue';

const route = useRoute();
const router = useRouter();
const store = useStore();
const { t } = useI18n();

const agentId = computed(() => route.params.agentId);

const summary = useMapGetter('pdvSalesReports/getSummary');
const agents = useMapGetter('pdvSalesReports/getAgents');
const products = useMapGetter('pdvSalesReports/getProducts');
const uiFlags = useMapGetter('pdvSalesReports/getUIFlags');

const agentName = computed(() => {
  const found = agents.value.find(
    a => String(a.agent_id) === String(agentId.value)
  );
  return found?.agent_name ?? `Agente #${agentId.value}`;
});

const now = Math.floor(Date.now() / 1000);
const defaultParams = { since: now - 30 * 86400, until: now, groupBy: 'day' };

const formatCurrency = val =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
    val || 0
  );

const metricCards = computed(() => [
  {
    key: 'total_revenue',
    label: t('PDV_SALES_REPORT.TOTAL_REVENUE'),
    value: formatCurrency(summary.value.total_revenue),
    infoText: t('PDV_SALES_REPORT.TOTAL_REVENUE'),
  },
  {
    key: 'total_sales',
    label: t('PDV_SALES_REPORT.TOTAL_SALES'),
    value: String(summary.value.total_sales ?? 0),
    infoText: t('PDV_SALES_REPORT.TOTAL_SALES'),
  },
  {
    key: 'avg_ticket',
    label: t('PDV_SALES_REPORT.AVG_TICKET'),
    value: formatCurrency(summary.value.avg_ticket),
    infoText: t('PDV_SALES_REPORT.AVG_TICKET'),
  },
  {
    key: 'total_discount',
    label: t('PDV_SALES_REPORT.TOTAL_DISCOUNT'),
    value: formatCurrency(summary.value.total_discount),
    infoText: t('PDV_SALES_REPORT.TOTAL_DISCOUNT'),
  },
  {
    key: 'avg_delivery_fee',
    label: t('PDV_SALES_REPORT.AVG_DELIVERY_FEE'),
    value: formatCurrency(summary.value.avg_delivery_fee),
    infoText: t('PDV_SALES_REPORT.AVG_DELIVERY_FEE'),
  },
]);

const fetchAgentData = () => {
  const params = { ...defaultParams, agentId: agentId.value };
  store.dispatch('pdvSalesReports/fetchSummary', params);
  store.dispatch('pdvSalesReports/fetchProducts', params);
};

onMounted(fetchAgentData);
watch(agentId, fetchAgentData);
</script>

<template>
  <div class="flex flex-col gap-6 p-6">
    <div class="flex items-center gap-3">
      <button
        class="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
        @click="router.back()"
      >
        <span class="i-lucide-arrow-left size-4" />
        {{ $t('generalSettings.BACK') }}
      </button>
      <h2 class="text-xl font-semibold text-slate-800 dark:text-slate-100">
        {{ agentName }}
      </h2>
    </div>

    <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
      <ReportMetricCard
        v-for="card in metricCards"
        :key="card.key"
        :label="card.label"
        :value="card.value"
        :info-text="card.infoText"
      />
    </div>

    <div
      class="bg-white dark:bg-slate-900 rounded-xl border border-slate-100 dark:border-slate-800 p-4"
    >
      <h3 class="text-sm font-semibold text-slate-700 dark:text-slate-200 mb-3">
        {{ $t('PDV_SALES_REPORT.TIMELINE_TITLE') }}
      </h3>
      <PdvSalesTimelineChart
        :timeseries="summary.timeseries || []"
        :is-loading="uiFlags.isFetchingSummary"
      />
    </div>

    <div
      class="bg-white dark:bg-slate-900 rounded-xl border border-slate-100 dark:border-slate-800 p-4"
    >
      <PdvSalesProductRanking
        :products="products"
        :is-loading="uiFlags.isFetchingProducts"
      />
    </div>
  </div>
</template>
